import { supabase } from '../lib/supabase'
import type {
  UserGroupRow,
  UserGroupInsert,
  UserGroupUpdate,
  UserGroupListRow,
} from '../types/database.types'
import { v4 as uuidv4 } from 'uuid'

/**
 * Service for managing user groups in Supabase
 * Groups are reusable collections of diagram components
 */

// List all groups for the current user
export const listUserGroups = async (): Promise<{
  data: UserGroupListRow[] | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase
      .from('user_groups_list')
      .select('*')
      .order('updated_at', { ascending: false })

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error listing groups:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to list groups'),
    }
  }
}

// Get a specific group by ID (with full data)
export const getGroup = async (
  id: string
): Promise<{
  data: UserGroupRow | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase
      .from('user_groups')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error getting group:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to get group'),
    }
  }
}

// Create a new group
export const createGroup = async (
  name: string,
  items: any[], // ModelItem[]
  options?: {
    description?: string
    category?: string
    icons?: any[] // Icon[]
    colors?: any[] // Color[]
    thumbnail?: string
    tags?: string[]
    isPublic?: boolean
  }
): Promise<{
  data: UserGroupRow | null
  error: Error | null
}> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('User not authenticated')
    }

    const insertData: UserGroupInsert = {
      user_id: user.id,
      name,
      description: options?.description || null,
      category: options?.category || 'custom',
      items,
      icons: options?.icons || [],
      colors: options?.colors || [],
      thumbnail: options?.thumbnail || null,
      tags: options?.tags || [],
      is_public: options?.isPublic || false,
    }

    const { data, error } = await supabase
      .from('user_groups')
      .insert(insertData as any)
      .select()
      .single()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error creating group:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to create group'),
    }
  }
}

// Update an existing group
export const updateGroup = async (
  id: string,
  updates: {
    name?: string
    description?: string
    category?: string
    items?: any[]
    icons?: any[]
    colors?: any[]
    thumbnail?: string
    tags?: string[]
    isPublic?: boolean
  }
): Promise<{
  data: UserGroupRow | null
  error: Error | null
}> => {
  try {
    const updateData: UserGroupUpdate = {}

    if (updates.name !== undefined) updateData.name = updates.name
    if (updates.description !== undefined) updateData.description = updates.description
    if (updates.category !== undefined) updateData.category = updates.category
    if (updates.items !== undefined) updateData.items = updates.items
    if (updates.icons !== undefined) updateData.icons = updates.icons
    if (updates.colors !== undefined) updateData.colors = updates.colors
    if (updates.thumbnail !== undefined) updateData.thumbnail = updates.thumbnail
    if (updates.tags !== undefined) updateData.tags = updates.tags
    if (updates.isPublic !== undefined) updateData.is_public = updates.isPublic

    const { data, error } = await supabase
      .from('user_groups')
      // @ts-expect-error - Supabase type inference issue with placeholder env variables
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error updating group:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to update group'),
    }
  }
}

// Delete a group
export const deleteGroup = async (
  id: string
): Promise<{
  success: boolean
  error: Error | null
}> => {
  try {
    const { error } = await supabase.from('user_groups').delete().eq('id', id)

    if (error) throw error

    return { success: true, error: null }
  } catch (error) {
    console.error('Error deleting group:', error)
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Failed to delete group'),
    }
  }
}

// Import a group into the current diagram
// This generates new IDs for items and adjusts their positions
export const importGroupToPosition = async (
  groupId: string,
  position: { x: number; y: number }
): Promise<{
  data: {
    items: any[] // New items with updated IDs and positions
    icons: any[] // Icons to merge
    colors: any[] // Colors to merge
  } | null
  error: Error | null
}> => {
  try {
    // Get the group data
    const { data: group, error: fetchError } = await getGroup(groupId)

    if (fetchError || !group) {
      throw fetchError || new Error('Group not found')
    }

    // Increment usage count
    await supabase.rpc('increment_group_usage', { group_uuid: groupId } as any)

    // Calculate bounding box of original items
    const originalItems = group.items
    if (originalItems.length === 0) {
      return { data: { items: [], icons: [], colors: [] }, error: null }
    }

    // Find min x and y to calculate offset
    let minX = Infinity
    let minY = Infinity

    originalItems.forEach((item: any) => {
      if (item.position) {
        minX = Math.min(minX, item.position.x)
        minY = Math.min(minY, item.position.y)
      }
    })

    // Calculate offset to move group to new position
    const offsetX = position.x - minX
    const offsetY = position.y - minY

    // Create ID mapping for items
    const idMap = new Map<string, string>()
    originalItems.forEach((item: any) => {
      idMap.set(item.id, uuidv4())
    })

    // Transform items with new IDs and positions
    const newItems = originalItems.map((item: any) => {
      const newItem = { ...item }

      // Assign new ID
      newItem.id = idMap.get(item.id) || uuidv4()

      // Update position
      if (newItem.position) {
        newItem.position = {
          x: newItem.position.x + offsetX,
          y: newItem.position.y + offsetY,
        }
      }

      // Update connector references
      if (newItem.connector) {
        if (newItem.connector.from && idMap.has(newItem.connector.from)) {
          newItem.connector.from = idMap.get(newItem.connector.from)
        }
        if (newItem.connector.to && idMap.has(newItem.connector.to)) {
          newItem.connector.to = idMap.get(newItem.connector.to)
        }
      }

      return newItem
    })

    return {
      data: {
        items: newItems,
        icons: group.icons || [],
        colors: group.colors || [],
      },
      error: null,
    }
  } catch (error) {
    console.error('Error importing group:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to import group'),
    }
  }
}

// Search groups by name or description
export const searchGroups = async (
  query: string
): Promise<{
  data: UserGroupRow[] | null
  error: Error | null
}> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase.rpc('search_user_groups', {
      search_query: query,
      user_uuid: user.id,
    } as any)

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error searching groups:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to search groups'),
    }
  }
}

// Get groups by category
export const getGroupsByCategory = async (
  category: string
): Promise<{
  data: UserGroupRow[] | null
  error: Error | null
}> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase.rpc('get_groups_by_category', {
      cat: category,
      user_uuid: user.id,
    } as any)

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error getting groups by category:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to get groups by category'),
    }
  }
}

// Get popular public groups (for inspiration/templates)
export const getPopularPublicGroups = async (
  limit: number = 20
): Promise<{
  data: UserGroupRow[] | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase.rpc('get_popular_public_groups', {
      limit_count: limit,
    } as any)

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error getting popular public groups:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to get popular groups'),
    }
  }
}

// Duplicate a group (create a copy)
export const duplicateGroup = async (
  id: string
): Promise<{
  data: UserGroupRow | null
  error: Error | null
}> => {
  try {
    // Get the original group
    const { data: original, error: fetchError } = await getGroup(id)

    if (fetchError || !original) {
      throw fetchError || new Error('Group not found')
    }

    // Create a copy with a new name
    const copyName = `${original.name} (Copy)`

    return await createGroup(copyName, original.items, {
      description: original.description || undefined,
      category: original.category || undefined,
      icons: original.icons,
      colors: original.colors,
      tags: original.tags,
      isPublic: false, // Copies are always private
    })
  } catch (error) {
    console.error('Error duplicating group:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to duplicate group'),
    }
  }
}

// Toggle group public status
export const toggleGroupPublic = async (
  id: string,
  isPublic: boolean
): Promise<{
  data: UserGroupRow | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase
      .from('user_groups')
      // @ts-expect-error - Supabase type inference issue with placeholder env variables
      .update({ is_public: isPublic })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error toggling group public status:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to update group visibility'),
    }
  }
}

// Get group count for current user
export const getUserGroupCount = async (): Promise<{
  count: number
  error: Error | null
}> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('User not authenticated')
    }

    const { count, error } = await supabase
      .from('user_groups')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)

    if (error) throw error

    return { count: count || 0, error: null }
  } catch (error) {
    console.error('Error getting group count:', error)
    return {
      count: 0,
      error: error instanceof Error ? error : new Error('Failed to get group count'),
    }
  }
}

// ============================================================================
// Group Instance Management Functions (for master-instance sync)
// ============================================================================

/**
 * Check if a master group is used in any diagrams
 * Prevents deletion if group has active instances
 */
export const isGroupInUse = async (
  groupId: string
): Promise<{
  inUse: boolean
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase.rpc('is_group_in_use', {
      group_uuid: groupId,
    } as any)

    if (error) throw error

    return { inUse: data === true, error: null }
  } catch (error) {
    console.error('Error checking if group is in use:', error)
    return {
      inUse: false,
      error: error instanceof Error ? error : new Error('Failed to check group usage'),
    }
  }
}

/**
 * Get all diagrams that use a specific master group
 * Returns diagram info and instance count
 */
export const getDiagramsUsingGroup = async (
  groupId: string
): Promise<{
  data: Array<{
    diagram_id: string
    diagram_title: string
    instance_count: number
    last_synced_at: string
  }> | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase.rpc('get_diagrams_using_group', {
      group_uuid: groupId,
    } as any)

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error getting diagrams using group:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to get diagrams using group'),
    }
  }
}

/**
 * Safely delete a master group
 * Blocks deletion if group is used in any diagrams
 */
export const safeDeleteGroup = async (
  groupId: string
): Promise<{
  success: boolean
  message: string
  diagramsUsingCount: number
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase.rpc('safe_delete_group', {
      group_uuid: groupId,
    } as any)

    if (error) throw error

    // RPC returns array with single result
    const result = (Array.isArray(data) ? data[0] : data) as {
      success: boolean
      message: string
      diagrams_using_count: number
    }

    if (!result) {
      throw new Error('No result returned from safe_delete_group')
    }

    return {
      success: result.success,
      message: result.message,
      diagramsUsingCount: result.diagrams_using_count,
      error: null,
    }
  } catch (error) {
    console.error('Error safely deleting group:', error)
    return {
      success: false,
      message: 'Failed to delete group',
      diagramsUsingCount: 0,
      error: error instanceof Error ? error : new Error('Failed to delete group'),
    }
  }
}

/**
 * Track a group instance in the database
 * Used for syncing and tracking which diagrams use which groups
 */
export const upsertGroupInstance = async (
  masterGroupId: string,
  diagramId: string,
  instanceId: string,
  masterGroupVersion: number,
  isSyncEnabled: boolean = true
): Promise<{
  data: any | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase.rpc('upsert_group_instance', {
      p_master_group_id: masterGroupId,
      p_diagram_id: diagramId,
      p_instance_id: instanceId,
      p_master_group_version: masterGroupVersion,
      p_is_sync_enabled: isSyncEnabled,
    } as any)

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error upserting group instance:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to track group instance'),
    }
  }
}
